import { useState } from 'react';
import { Bell, ChevronDown, DatabaseZap, Loader2, LogOut, Search, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { hasCentralAccess, signOut, useSession } from '../auth/session.js';
import { useDemoData, type DemoPhase } from '../api/useDemoData.js';
import { ResetDemoDialog } from './ResetDemoDialog.js';
import './topbar.css';

interface TopbarProps {
  userName: string;
  userRole: string;
  onSeeded?: () => void;
}

const PHASE_LABELS: Record<DemoPhase, { label: string; hint: string }> = {
  before: {
    label: 'Corpus de référence',
    hint: 'Remplace la mesure du projet de démonstration par le corpus de référence, puis exécute toute la chaîne.',
  },
  after: {
    label: 'Corpus après publication',
    hint: 'Ajoute quatorze jours de sessions mesurées sous la version publiée, friction réduite sur les seules étapes guidées, puis exécute la chaîne. Une guidance doit être publiée.',
  },
};

export function Topbar({ userName, userRole, onSeeded }: TopbarProps) {
  const demo = useDemoData();
  // Generating measurement is a central access operation; a platform access is refused by the API anyway.
  const central = hasCentralAccess(useSession());
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  const run = (phase: DemoPhase) => {
    setOpen(false);
    setError(null);
    demo.seed.mutate(phase, {
      onSuccess: () => onSeeded?.(),
      onError: (cause) => setError(cause instanceof Error ? cause.message : 'La génération a échoué'),
    });
  };

  return (
    <header className="topbar">
      <label className="topbar__search">
        <Search size={15} strokeWidth={1.9} />
        <input
          type="search"
          placeholder="Rechercher un parcours, une étape, une guidance…"
          aria-label="Rechercher"
        />
        <kbd>⌘K</kbd>
      </label>

      <div className="topbar__right">
        {demo.available && central && (
          <div className="topbar__demo">
            <button
              className="topbar__seed"
              onClick={() => setOpen((value) => !value)}
              disabled={demo.seed.isPending || demo.reset.isPending}
              aria-haspopup="menu"
              aria-expanded={open}
              title="Génère un corpus de mesure réaliste puis exécute toute la chaîne : graphe, parcours, guidances proposées, mesure avant / après."
            >
              {demo.seed.isPending ? (
                <Loader2 size={14} className="topbar__spin" />
              ) : (
                <DatabaseZap size={14} />
              )}
              {demo.seed.isPending ? 'Génération…' : 'Données de démo'}
            </button>
            {open && !demo.seed.isPending && (
              <div className="topbar__demo-menu" role="menu" aria-label="Données de démonstration">
                {(Object.keys(PHASE_LABELS) as DemoPhase[]).map((phase) => (
                  <button
                    key={phase}
                    type="button"
                    role="menuitem"
                    className="topbar__demo-item"
                    onClick={() => run(phase)}
                  >
                    <span className="topbar__demo-label">{PHASE_LABELS[phase].label}</span>
                    <span className="topbar__demo-hint">{PHASE_LABELS[phase].hint}</span>
                  </button>
                ))}
                <button
                  type="button"
                  role="menuitem"
                  className="topbar__demo-item topbar__demo-item--danger"
                  onClick={() => {
                    setOpen(false);
                    setError(null);
                    demo.reset.reset();
                    setResetting(true);
                  }}
                >
                  <span className="topbar__demo-label">Remettre à zéro</span>
                  <span className="topbar__demo-hint">
                    Efface sessions, parcours, recommandations et aides du projet de démonstration. Le projet,
                    sa balise et les comptes restent.
                  </span>
                </button>
              </div>
            )}
            {resetting && (
              <ResetDemoDialog
                busy={demo.reset.isPending}
                error={demo.reset.error instanceof Error ? demo.reset.error.message : null}
                onCancel={() => setResetting(false)}
                onConfirm={(confirmation) =>
                  demo.reset.mutate(confirmation, {
                    onSuccess: () => {
                      setResetting(false);
                      onSeeded?.();
                    },
                  })
                }
              />
            )}
            {error && (
              <p className="topbar__demo-error" role="alert">
                {error}
              </p>
            )}
          </div>
        )}

        <button className="topbar__icon-btn" aria-label="Notifications">
          <Bell size={16} strokeWidth={1.9} />
          <span className="topbar__dot" aria-hidden />
        </button>

        <UserMenu userName={userName} userRole={userRole} />
      </div>
    </header>
  );
}

/** Who is signed in, the way to the profile, and sign-out at all times (functional §9.2). */
function UserMenu({ userName, userRole }: { userName: string; userRole: string }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  return (
    <div className="topbar__user-menu">
      <button
        type="button"
        className="topbar__user"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="topbar__avatar" aria-hidden>
          {initials(userName)}
        </span>
        <span className="topbar__user-text">
          <span className="topbar__user-name">{userName}</span>
          <span className="topbar__user-role">{userRole}</span>
        </span>
        <ChevronDown size={14} className="topbar__chevron" aria-hidden />
      </button>
      {open && (
        <div className="topbar__menu" role="menu" aria-label="Compte">
          <Link to="/profile" role="menuitem" className="topbar__menu-item" onClick={() => setOpen(false)}>
            <UserRound size={14} /> Mon profil
          </Link>
          <button
            type="button"
            role="menuitem"
            className="topbar__menu-item"
            onClick={() => {
              setOpen(false);
              queryClient.clear();
              void signOut();
            }}
          >
            <LogOut size={14} /> Se déconnecter
          </button>
        </div>
      )}
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
